// src/components/analytics/predictors/MovingAveragePredictor.js

import { BasePredictor } from './base/BasePredictor';
import { 
  parseDate, 
  formatDate, 
  dateToNumeric, 
  numericToDate, 
  analyzeDateFrequency,
  generateFutureDatesWithFrequency,
  detectDateFormat,
  addDays
} from '../utils/dateUtils';

export class MovingAveragePredictor extends BasePredictor {
  constructor(config) {
    super(config);
    this.method = 'moving_average';
    
    // Set default window size if not provided
    this.windowSize = config.windowSize || 3;
    
    // Validate window size
    if (this.windowSize < 1) {
      throw new Error('Window size must be at least 1');
    }
  }

  /**
   * Calculate simple moving average for a series
   * @param {Array} values - Array of numeric values
   * @param {number} windowSize - Size of the moving window
   * @returns {Array} - Array of moving averages
   */
  calculateMovingAverage(values, windowSize) {
    const movingAverages = [];
    
    for (let i = 0; i < values.length; i++) {
      if (i < windowSize - 1) {
        // Not enough data points yet, use what we have
        const availableValues = values.slice(0, i + 1);
        const sum = availableValues.reduce((acc, val) => acc + val, 0);
        movingAverages.push(sum / availableValues.length);
      } else {
        // Calculate moving average for the window
        const windowValues = values.slice(i - windowSize + 1, i + 1);
        const sum = windowValues.reduce((acc, val) => acc + val, 0);
        movingAverages.push(sum / windowSize);
      }
    }
    
    return movingAverages;
  }

  /**
   * Calculate weighted moving average
   * @param {Array} values - Array of numeric values
   * @param {number} windowSize - Size of the moving window
   * @returns {number} - Weighted average for prediction
   */
  calculateWeightedAverage(values, windowSize) {
    // Use last windowSize values
    const lastValues = values.slice(-windowSize);
    
    // Generate weights (more recent = higher weight)
    const weights = [];
    for (let i = 1; i <= lastValues.length; i++) {
      weights.push(i);
    }
    
    // Calculate weighted sum
    let weightedSum = 0;
    let weightSum = 0;
    
    for (let i = 0; i < lastValues.length; i++) {
      weightedSum += lastValues[i] * weights[i];
      weightSum += weights[i];
    }
    
    return weightedSum / weightSum;
  }

  /**
   * Main prediction method
   * @param {Array} data - The data to analyze
   * @param {string} xAxis - The x-axis column name
   * @param {string} yAxis - The y-axis column name
   * @returns {Object} - { predictions: Array, metadata: Object }
   */
  predict(data, xAxis, yAxis) {
    // Validate data
    const validation = this.validateData(data, xAxis, yAxis);
    if (!validation.valid) {
      throw new Error(validation.error);
    }

    // Safety check for prediction limit
    const maxAllowedPredictions = 365;
    if (this.config.numPredictions && this.config.numPredictions > maxAllowedPredictions) {
      console.warn(`Limiting predictions from ${this.config.numPredictions} to ${maxAllowedPredictions}`);
      this.config.numPredictions = maxAllowedPredictions;
    }

    // Extract time series
    const timeSeries = this.extractTimeSeries(
      data, 
      xAxis, 
      yAxis, 
      this.config.aggregationMethod || 'average'
    );

    if (timeSeries.length < this.windowSize) {
      throw new Error(`Insufficient data points for moving average. Need at least ${this.windowSize} points, got ${timeSeries.length}`);
    }

    // Extract y values for calculation
    const yValues = timeSeries.map(point => point.y);
    
    // Calculate moving averages for historical data
    const historicalMA = this.calculateMovingAverage(yValues, this.windowSize);
    
    // Check if x-axis is dates
    const firstX = timeSeries[0].x;
    const isDateAxis = this.isDateValue(firstX);
    
    let futureXValues = [];
    let dateFormat = null;
    let originalDateFormat = null; // Store the actual format from data
    let frequency = null;
    let averageInterval = null;

    if (isDateAxis) {
      // Store original date format from the first data point
      if (timeSeries.length > 0) {
        originalDateFormat = timeSeries[0].x;
      }
      
      // Parse dates
      const dates = timeSeries.map(point => parseDate(point.x)).filter(d => d !== null);
      
      if (dates.length < 2) {
        throw new Error('Insufficient valid date points for prediction');
      }

      // Detect date format and frequency
      dateFormat = detectDateFormat(timeSeries[0].x);
      const frequencyAnalysis = analyzeDateFrequency(dates);
      frequency = frequencyAnalysis.frequency;
      averageInterval = frequencyAnalysis.averageInterval;

      // Generate future dates
      const lastDate = dates[dates.length - 1];
      const predictUntil = this.config.predictUntil || null;
      
      const futureDates = generateFutureDatesWithFrequency(
        lastDate,
        predictUntil,
        frequency,
        averageInterval
      );

      // Limit number of predictions
      const maxPredictions = Math.min(this.config.numPredictions || 10, maxAllowedPredictions);

      // Use the shared method from BasePredictor to calculate predictions needed
      const numPredictions = this.calculatePredictionsNeeded(
        lastDate,
        predictUntil,
        frequency,
        averageInterval
      );

      const limitedDates = futureDates.slice(0, numPredictions);

      futureXValues = limitedDates.map(date => ({
        date: date,
        formatted: originalDateFormat ? formatDate(date, originalDateFormat) : formatDate(date, dateFormat || 'yyyy-MM-dd')
      }));
    } else {
      // Generate numeric future values
      const lastX = parseFloat(timeSeries[timeSeries.length - 1].x);
      const avgInterval = timeSeries.length > 1 
        ? (lastX - parseFloat(timeSeries[0].x)) / (timeSeries.length - 1)
        : 1;
      
      const numPredictions = Math.min(this.config.numPredictions || 10, maxAllowedPredictions);
      
      for (let i = 1; i <= numPredictions; i++) {
        futureXValues.push({
          formatted: String(lastX + (avgInterval * i))
        });
      }
    }

    // Generate predictions using different methods based on config
    const predictionMethod = this.config.predictionMethod || 'weighted';
    const predictions = [];

    if (predictionMethod === 'simple') {
      // Simple method: use the last moving average value
      const lastMA = historicalMA[historicalMA.length - 1];
      
      futureXValues.forEach((future, index) => {
        predictions.push({
          x: future.formatted,
          y: lastMA,
          _futureIndex: index,
          _isPrediction: true,
          _method: 'simple_ma'
        });
      });
    } else if (predictionMethod === 'weighted') {
      // Weighted method: use weighted average of recent values
      const baseValue = this.calculateWeightedAverage(yValues, this.windowSize);
      
      futureXValues.forEach((future, index) => {
        predictions.push({
          x: future.formatted,
          y: baseValue,
          _futureIndex: index,
          _isPrediction: true,
          _method: 'weighted_ma'
        });
      });
    } else {
      // Trend-following method: use recent trend
      const recentMA = historicalMA.slice(-this.windowSize);
      const trend = recentMA.length > 1 
        ? (recentMA[recentMA.length - 1] - recentMA[0]) / (recentMA.length - 1)
        : 0;
      
      let lastValue = historicalMA[historicalMA.length - 1];
      
      futureXValues.forEach((future, index) => {
        const predictedValue = lastValue + (trend * (index + 1));
        
        predictions.push({
          x: future.formatted,
          y: predictedValue,
          _futureIndex: index,
          _isPrediction: true,
          _method: 'trend_ma'
        });
      });
    }

    // Add confidence intervals if requested
    if (this.config.includeConfidenceInterval) {
      // Calculate standard deviation of recent values
      const recentValues = yValues.slice(-this.windowSize);
      const mean = recentValues.reduce((sum, val) => sum + val, 0) / recentValues.length;
      const variance = recentValues.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / recentValues.length;
      const stdDev = Math.sqrt(variance);
      
      // Add confidence bands (simple approach)
      const confidenceMultiplier = this.config.confidenceLevel === 0.95 ? 1.96 : 2.58;
      
      predictions.forEach(pred => {
        pred.confidenceLower = pred.y - (confidenceMultiplier * stdDev);
        pred.confidenceUpper = pred.y + (confidenceMultiplier * stdDev);
      });
    }

    // Format output
    const formattedPredictions = this.formatPredictionOutput(predictions, xAxis, yAxis, data[0]);

    // Add historical moving averages to metadata if requested
    let enhancedData = null;
    if (this.config.includeHistoricalMA) {
      enhancedData = timeSeries.map((point, index) => ({
        ...point,
        movingAverage: historicalMA[index]
      }));
    }

    // Return predictions with metadata
    return {
      predictions: formattedPredictions,
      metadata: {
        method: this.method,
        windowSize: this.windowSize,
        predictionMethod: predictionMethod,
        dataPoints: timeSeries.length,
        isTimeSeries: isDateAxis,
        frequency: frequency,
        averageInterval: averageInterval,
        lastMovingAverage: historicalMA[historicalMA.length - 1],
        historicalMovingAverages: enhancedData,
        config: this.config
      }
    };
  }

  /**
   * Check if a value represents a date
   * @param {any} value - Value to check
   * @returns {boolean}
   */
  isDateValue(value) {
    if (!value) return false;
    
    const stringValue = String(value);
    
    // Check common date patterns
    const datePatterns = [
      /^\d{4}-\d{2}-\d{2}/,     // YYYY-MM-DD
      /^\d{2}\/\d{2}\/\d{4}/,   // DD/MM/YYYY or MM/DD/YYYY
      /^\d{4}\/\d{2}\/\d{2}/,   // YYYY/MM/DD
      /^\d{4}-\d{2}$/,          // YYYY-MM
      /^[A-Za-z]+ \d{4}$/       // Month YYYY
    ];

    return datePatterns.some(pattern => pattern.test(stringValue));
  }
}

export default MovingAveragePredictor;